<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <header
    ref="navElement"
    class="app-header border-b border-white/50 px-6 py-3 sm:py-4 shadow-sm"
    v-bind="$attrs"
  >
    <div class="space-between gap-3 sm:gap-4 md:gap-7 w-full max-w-7xl mx-auto">
      <!-- Le carré est imposé au conteneur : sans largeur explicite, Safari résout le `w-full` de
           l'image sur sa largeur native (500px) et le logo, étiré, chasse le reste de l'en-tête. -->
      <div class="shrink-0 aspect-square h-16 sm:h-18 md:h-24">
        <router-link append :to="{ name: RoutesKey.HOME }" class="block h-full w-full">
          <img
            class="h-full w-full object-contain"
            src="../../assets/images/logo-sigcom.png"
            alt="logo-sigcom"
          />
        </router-link>
      </div>

      <div class="flex-1 grown">
        <!--
          Un exécutif communal n'administre que sa commune : le sélecteur reste visible pour situer
          le compte, mais grisé et figé sur son territoire.
        -->
        <div
          class="relative"
          :class="{ 'cursor-not-allowed': communeVerrouillee }"
          :title="
            communeVerrouillee
              ? $t('header.communeVerrouillee', { commune: nomCommuneRattachee })
              : undefined
          "
        >
          <div
            class="absolute inset-y-0 left-0 z-[2] flex items-center pl-4 pointer-events-none"
            :class="communeVerrouillee ? 'text-gray-400' : 'text-white'"
          >
            <Icon
              :icon="
                communeVerrouillee ? 'heroicons-outline:lock-closed' : 'heroicons-outline:search'
              "
              size="20"
            />
          </div>
          <CommuneSearch
            selection-behavior="navigate"
            :chrome-tone="communeVerrouillee ? 'onLight' : 'onDark'"
            :shell-class="communeVerrouillee ? SHELL_VERROUILLE : SHELL_ACTIF"
            :input-class="communeVerrouillee ? INPUT_VERROUILLE : INPUT_ACTIF"
            options-class="!z-[100]"
            :disabled="communeVerrouillee"
            :placeholder-override="
              communeVerrouillee && nomCommuneRattachee
                ? $t('header.communeDe', { commune: nomCommuneRattachee })
                : $t('filters.selectCom')
            "
          />
        </div>
      </div>

      <router-link
        v-if="userStore.userIsLogged"
        :to="{ name: RoutesKey.NATURAL_LANGUAGE_QUERY }"
        class="shrink-0 flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl bg-main hover:bg-main-hover text-white font-medium text-sm transition-colors"
      >
        <Icon icon="heroicons:sparkles-20-solid" size="20" />
        <span class="hidden sm:inline">SIGCOM-GPT</span>
      </router-link>

      <div class="flex items-center gap-2 sm:gap-3 md:gap-6 shrink-0">
        <!-- Le choix de langue précède le compte : il vaut pour les visiteurs comme pour les
             utilisateurs connectés, là où le menu suivant suppose une session. Sur téléphone, un
             compte connecté le retrouve dans son menu de profil, faute de place dans l'en-tête. -->
        <LanguageSwitcher :class="{ 'hidden sm:block': userStore.userIsLogged }" />

        <div v-if="userStore.userIsLogged" class="relative user-info">
          <button
            @click="toggleUserMenu"
            class="flex items-center hover:bg-slate-50 transition-all duration-200 p-2 rounded-lg"
          >
            <div class="w-10 h-10 rounded-full overflow-hidden md:mr-2 border border-gray-200">
              <img
                v-if="currentUser?.logo"
                :src="baseUrl + currentUser.logo.contentUrl"
                :alt="currentUser?.userNom || 'User profile'"
                class="w-full h-full object-cover"
              />
              <DefaultAvatar sizeStyle="40" v-else />
            </div>

            <div class="text-left hidden md:block">
              <p class="font-medium">{{ currentUser?.userNom }}</p>
              <p class="text-sm text-gray-400">{{ currentUser?.userPrenom }}</p>
            </div>

            <svg
              class="w-4 h-4 transition-transform -mt-4 hidden md:block"
              :class="{ 'rotate-180': isUserMenuOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <div
              v-if="isUserMenuOpen"
              class="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/80 py-1 z-50"
            >
              <div
                class="stack-2 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors cursor-pointer"
                @click="router.push({ name: RoutesKey.USERACCOUNT })"
              >
                <Icon icon="gg:profile" color="color-black" />
                <span>{{ $t('header.account') }}</span>
              </div>
              <div
                v-if="userStore.userGouverneUneCommune"
                class="stack-2 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors cursor-pointer"
                @click="router.push({ name: RoutesKey.DASHBOARD_MAIRE })"
              >
                <Icon icon="heroicons-outline:chart-pie" color="black" />
                <span>{{ $t('header.dashboard') }}</span>
              </div>
              <div
                v-if="userStore.userPeutConsulterDashboardCentral"
                class="stack-2 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors cursor-pointer"
                @click="router.push({ name: RoutesKey.DASHBOARD_CENTRAL })"
              >
                <Icon icon="heroicons-outline:globe-alt" color="black" />
                <span>{{ $t('header.dashboardCentral') }}</span>
              </div>
              <div
                v-if="userStore.userAccedeAdministration()"
                class="stack-2 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors cursor-pointer"
                @click="router.push({ name: RoutesKey.ADMIN })"
              >
                <Icon icon="system-uicons:filtering" color="black" />
                <span>{{ $t('header.administration') }}</span>
              </div>
              <div class="sm:hidden border-y border-gray-200/80 my-1 py-1">
                <button
                  v-for="code in LOCALES_DISPONIBLES"
                  :key="code"
                  type="button"
                  class="stack-2 w-full px-4 py-2 text-sm text-left hover:bg-slate-100 transition-colors"
                  :class="locale === code ? 'text-main font-semibold' : 'text-gray-700'"
                  @click="appliquerLocale(code)"
                >
                  <Icon icon="heroicons-outline:globe-alt" color="black" />
                  <span>{{ LIBELLES_LANGUES[code] }}</span>
                </button>
              </div>
              <div
                class="stack-2 px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors cursor-pointer"
                @click="userStore.signOut()"
              >
                <Icon icon="material-symbols-light:logout-rounded" color="black" />
                <span>{{ $t('header.logout') }}</span>
              </div>
            </div>
          </Transition>
        </div>
        <div v-else>
          <router-link
            class="shrink-0 flex items-center justify-center gap-2 sm:min-w-[10.25rem] px-3 sm:px-4 py-2.5 rounded-xl bg-main hover:bg-main-hover text-white font-medium text-sm transition-colors"
            append
            :to="{ name: RoutesKey.AUTH_SIGN_IN }"
            :aria-label="$t('header.login')"
          >
            <Icon icon="heroicons-outline:arrow-right-on-rectangle" size="20" />
            <span class="hidden sm:inline">{{ $t('header.login') }}</span>
          </router-link>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false })
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { gsap, Power1 } from 'gsap'
import { useUserStore } from '@/stores/userStore'
import DefaultAvatar from '../ui/DefaultAvatar.vue'
import { useRouter } from 'vue-router'
import { RoutesKey } from '@/models/enums/app/RoutesKey'
import CommuneSearch from '@/components/forms/CommuneSearch.vue'
import Icon from '../ui/Icon.vue'
import { apiBaseUrl as baseUrl } from '@/config/env'
import LanguageSwitcher from './LanguageSwitcher.vue'
import { appliquerLocale, LOCALES_DISPONIBLES, type LocaleDisponible } from '@/plugins/i18n'
import { useI18n } from 'vue-i18n'

const userStore = useUserStore()
const isUserMenuOpen = ref(false)
const showHeader = ref(true)
const navElement = ref<HTMLElement | null>(null)
const currentUser = computed(() => userStore.currentUser)
const router = useRouter()
const { locale } = useI18n()

// Chaque langue est nommée dans sa propre langue, comme dans LanguageSwitcher.
const LIBELLES_LANGUES: Record<LocaleDisponible, string> = { fr: 'Français', en: 'English' }

/**
 * Un profil communal (maire, admin technique, agent, lecteur local) n'opère que sur la commune de
 * son compte : changer de commune depuis l'en-tête l'enverrait sur un territoire qui n'est pas le
 * sien. Les comptes centraux gardent la main, même s'ils cumulent un rôle local.
 */
const communeVerrouillee = computed(() => userStore.userEstCommunal)
const nomCommuneRattachee = computed(() => currentUser.value?.commune?.cmnTitre ?? '')

const SHELL_ACTIF =
  'relative flex w-full items-center pl-12 pr-4 h-11 sm:h-14 md:h-16 text-white bg-main/90 border border-main/30 rounded-xl text-sm transition-all focus-within:outline-none focus-within:ring-2 focus-within:ring-main focus-within:ring-offset-2 focus-within:border-main'
const INPUT_ACTIF =
  'h-full min-w-0 w-full flex-1 border-0 bg-transparent py-0 text-sm text-white placeholder:text-white/90 focus:outline-none focus:ring-0'
const SHELL_VERROUILLE =
  'relative flex w-full items-center pl-12 pr-4 h-11 sm:h-14 md:h-16 bg-gray-100 border border-gray-200 rounded-xl text-sm pointer-events-none select-none'
const INPUT_VERROUILLE =
  'h-full min-w-0 w-full flex-1 border-0 bg-transparent py-0 text-sm font-medium text-gray-500 placeholder:text-gray-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed'

/* const currentUrl = computed(() => window.location.href)

const shareBody = computed(() => {
  return t('share.genericBody') + ' ' + currentUrl.value
})
async function shareWithLink() {
  await navigator.clipboard.writeText(currentUrl.value)
  addNotification(t('share.urlCopied'), NotificationType.SUCCESS)
}

function shareByEmail() {
  const mailto = `mailto:?subject=${encodeURIComponent(t('share.mailSubject'))}&body=${encodeURIComponent(shareBody.value)}`
  window.location.href = mailto
}

function shareOnWhatsapp() {
  const whatsappLink = `https://wa.me/?text=${currentUrl.value}`
  window.open(whatsappLink, '_blank')
}

function shareOnFacebook() {
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${currentUrl.value}`
  window.open(facebookShareUrl, '_blank', 'width=600,height=400')
} */
const removeRouterGuard = router.afterEach(() => {
  const mainContainer = document.querySelector('html') as HTMLElement
  if (mainContainer) {
    mainContainer.style.paddingTop = '0px'
  }
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
})

const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value
}

// Permet de fermer les menus utilisateur et langue en cliquant en dehors de ceux-ci
const closeMenuOnClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.user-info')) {
    isUserMenuOpen.value = false
  }
}

onMounted(() => {
  window.addEventListener('scroll', onScroll)
  document.addEventListener('click', closeMenuOnClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', closeMenuOnClickOutside)
  removeRouterGuard()
  window.removeEventListener('scroll', onScroll)
})

function onScroll() {
  const mainContainer = document.querySelector('html') as HTMLElement
  const nav = navElement.value
  if (nav === null) {
    return
  }

  if (window.scrollY > 127) {
    nav.classList.add('top-nav')
    mainContainer.style.paddingTop = '64px'
    if (showHeader.value) {
      if (nav !== null) {
        nav.style.top = '-100px'
        showHeader.value = false
      }
    }

    gsap.to(nav, {
      top: 0,
      ease: Power1.easeOut,
      duration: 0.3,
    })
  } else if (window.scrollY === 0) {
    showHeader.value = true
    nav.classList.remove('top-nav')
    mainContainer.style.paddingTop = '0px'
  }
}
</script>

<style lang="scss" scoped>
.app-header {
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.scroll-top {
  height: 44px;
  width: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: black;
  position: fixed;
  z-index: 10;
  bottom: 20px;
  right: 12px;
  &:after {
    background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.25) 0, transparent 80%);
    content: '';
    height: 10px;
    position: absolute;
    top: 105%;
    width: 90%;
    z-index: -1;
  }
}
.nav {
  width: 100vw;
  z-index: 100 !important;
}

.top-nav {
  position: fixed;
  width: 100%;
  left: 0;
  z-index: 5;
  padding-right: 25px;
  scrollbar-gutter: stable;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 24px -4px rgba(0, 0, 0, 0.08);
  top: -127px;
}
</style>
